import type { PropsWithChildren, ReactNode } from 'react';
import React from 'react';

import { Navigate } from 'react-router';

import { pipe } from '@code-expert/prelude';
import type { ExamOption } from '/imports/api/hooks/client/courseSelect';
import type { CourseUrl, ExamId, SemesterId } from '/imports/domain';
import { concatRoutes, formatRoute, parser } from '/imports/modules/Routing';
import { Authorise } from '/imports/ui/components/Authorise';
import { MainContent } from '/imports/ui/components/MainContent';
import { TopNavLayout } from '/imports/ui/layouts/TopNavLayout';
import type { Page } from './routes';
import {
  createRoute,
  editRoute,
  examRoute,
  manageRoute,
  messagesRoute,
  myExamsRoute,
  overviewRoute,
  preparationRoute,
  progressRoute,
  regradeRoute,
  regradeTaskRoute,
  reviewRoute,
  submissionsRoute,
  userCheckRoute,
} from './routes';

const Navigation = React.lazy(() => import('./Navigation'));
const Entry = React.lazy(() => import('./Entry'));
const StudentOverview = React.lazy(
  () => import('/imports/ui/pages/myCourses/studentOverviewTable/StudentOverview'),
);
const LiveSubmissions = React.lazy(
  () => import('/imports/ui/pages/myCourses/submissions/LiveSubmissions'),
);
const ExamUserCheck = React.lazy(() => import('./execution/ExamUserCheck'));
const ExamUserOverview = React.lazy(() => import('./execution/ExamUserOverview'));
const ExamEdit = React.lazy(() => import('./preparation/ExamEdit'));
const ExamNew = React.lazy(() => import('./preparation/ExamNew'));
const ExamOverview = React.lazy(() => import('./preparation/ExamOverview'));
const RegradeExam = React.lazy(() => import('./regrade/RegradeExam'));
const ExamReview = React.lazy(() => import('./review/ExamReview'));
const ExamStatus = React.lazy(() => import('./status'));
const Messages = React.lazy(() => import('/imports/ui/pages/myCourses/messages/Messages'));

const MyExamsWrapper = ({ children }: PropsWithChildren) => (
  <Authorise
    allow={['courseAdmin']}
    layout={(children) => <TopNavLayout selected="myexams">{children}</TopNavLayout>}
  >
    {children}
  </Authorise>
);

const MyExamsPage = ({
  page,
  semester,
  courseUrl,
  examId,
  children,
}: {
  page: Page;
  semester: SemesterId;
  courseUrl: CourseUrl;
  examId: ExamId;
  children: (exam: ExamOption) => ReactNode;
}) => (
  <MyExamsWrapper>
    <Navigation page={page} semester={semester} courseUrl={courseUrl} examId={examId}>
      {children}
    </Navigation>
  </MyExamsWrapper>
);

export const myExamsRouter = concatRoutes([
  pipe(
    editRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsPage page="examEdit" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => (
          <ExamEdit examId={_id} courseId={courseId} courseUrl={courseUrl} semester={semester} />
        )}
      </MyExamsPage>
    )),
  ),
  pipe(
    userCheckRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsPage page="userCheck" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id }) => <ExamUserCheck examId={_id} />}
      </MyExamsPage>
    )),
  ),
  pipe(
    overviewRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsPage page="overview" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => <ExamUserOverview examId={_id} courseId={courseId} />}
      </MyExamsPage>
    )),
  ),
  pipe(
    preparationRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsPage page="examPreparation" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => (
          <ExamOverview
            examId={_id}
            courseUrl={courseUrl}
            courseId={courseId}
            semester={semester}
          />
        )}
      </MyExamsPage>
    )),
  ),
  pipe(
    manageRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsPage page="examStatus" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => <ExamStatus examId={_id} courseId={courseId} semester={semester} />}
      </MyExamsPage>
    )),
  ),
  pipe(
    messagesRoute.parser,
    parser.map(({ semester, courseUrl, examId, section }) => (
      <MyExamsPage page="messages" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => (
          <Messages
            examId={_id}
            courseId={courseId}
            semester={semester}
            section={section}
            sectionLink={(tab) =>
              formatRoute(messagesRoute)({ semester, courseUrl, examId, section: tab })
            }
          />
        )}
      </MyExamsPage>
    )),
  ),
  pipe(
    submissionsRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsPage page="submissions" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => (
          <LiveSubmissions examId={_id} courseId={courseId} semester={semester} />
        )}
      </MyExamsPage>
    )),
  ),
  pipe(
    progressRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsPage page="progress" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => (
          <MainContent title="Progress">
            <StudentOverview
              examId={_id}
              courseId={courseId}
              semester={semester}
              courseUrl={courseUrl}
              adminView
            />
          </MainContent>
        )}
      </MyExamsPage>
    )),
  ),
  pipe(
    regradeRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsPage page="regrade" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => (
          <RegradeExam courseId={courseId} courseUrl={courseUrl} semester={semester} examId={_id} />
        )}
      </MyExamsPage>
    )),
  ),
  pipe(
    regradeTaskRoute.parser,
    parser.map(({ semester, courseUrl, examId, taskId }) => (
      <MyExamsPage page="regrade" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => (
          <RegradeExam
            courseId={courseId}
            courseUrl={courseUrl}
            semester={semester}
            examId={_id}
            taskId={taskId}
          />
        )}
      </MyExamsPage>
    )),
  ),
  pipe(
    reviewRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsPage page="examReview" semester={semester} courseUrl={courseUrl} examId={examId}>
        {({ _id, courseId }) => <ExamReview courseId={courseId} examId={_id} />}
      </MyExamsPage>
    )),
  ),
  pipe(
    examRoute.parser,
    parser.map(({ semester, courseUrl, examId }) => (
      <MyExamsWrapper>
        <Navigate to={formatRoute(preparationRoute)({ semester, courseUrl, examId })} />
      </MyExamsWrapper>
    )),
  ),
  pipe(
    createRoute.parser,
    parser.map(() => (
      <MyExamsWrapper>
        <ExamNew />
      </MyExamsWrapper>
    )),
  ),
  pipe(
    myExamsRoute.parser,
    parser.map(() => (
      <MyExamsWrapper>
        <Entry />
      </MyExamsWrapper>
    )),
  ),
]);
